import { useManager } from 'dreamstate';
import { useEffect, useRef, useState } from 'react';

import { ReviewQuestion } from '@vimeo/bokeh-icons';

import { IconNotification } from './IconNotification';
import {
  IQnAContext,
  QnAManager,
  useQnaQuestions,
} from '../../../../store/firebase/interaction/qna';
import { IQnAQuestion } from '../../../../types/interaction_tools';
import { IInteractionStoreInitialState } from '../../../../types/store';
import { useLiveGlobals } from '../../../providers/globals';

/**
 * Hook for detect changes in qna and set true flag for notification.
 * @param isActive represents that qna is active module
 * @param isManagementAccessed represents that qna used on moderator or viewer side
 */

interface IPanelQnaIconProps {
  isActive: boolean;
  isManagementAccessed: boolean;
  qnaContext?: IQnAContext;
}

export function PanelQnaIcon({
  isActive,
  isManagementAccessed,
  qnaContext: {
    activeSession,
    isEventModerated,
    activeSessionQuestions,
    activeSessionPendingQuestions,
    // eslint-disable-next-line react-hooks/rules-of-hooks
  } = useManager(QnAManager),
}: IPanelQnaIconProps) {
  const {
    initialState: { renderAt },
  } = useLiveGlobals<IInteractionStoreInitialState>();

  const lastSeen = useRef<number>((renderAt as number) + 100); // +100 for rendering delay
  const [hasNotification, setHasNotification] = useState<boolean>(false);

  const { pendingQuestions, activeQuestions } = useQnaQuestions(
    activeSessionPendingQuestions,
    activeSessionQuestions,
  );

  const questions: Array<IQnAQuestion> = isManagementAccessed
    ? isEventModerated
      ? pendingQuestions
      : activeQuestions
    : activeQuestions;

  const lastQuestion: IQnAQuestion = questions[questions.length - 1];

  useEffect(() => {
    setHasNotification((hasNotification) => {
      if (lastQuestion) {
        if (isActive) {
          lastSeen.current = lastQuestion.createdAt;
          return false;
        } else {
          return lastQuestion.createdAt > lastSeen.current;
        }
      }

      if (activeSession) {
        const activeSessionUpdatedAt = activeSession.startedAt || activeSession.createdAt;

        if (isActive) {
          lastSeen.current = activeSessionUpdatedAt;
          return false;
        } else if (activeSessionUpdatedAt > lastSeen.current !== hasNotification) {
          return true;
        }
      }

      return false;
    });
  }, [isActive, lastQuestion, activeSession]);

  return (
    <IconNotification isVisible={hasNotification}>
      <ReviewQuestion />
    </IconNotification>
  );
}
